import React from "react";
import Select, { Props as SelectProps, StylesConfig, GroupBase } from "react-select";

interface SelectFieldProps<
  OptionType = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>
> extends SelectProps<OptionType, IsMulti, Group> {
  error?: string;
  touched?: boolean;
  containerClassName?: string;
}

const defaultStyles: StylesConfig = {
  control: (base) => ({
    ...base,
    minHeight: "44px",
    fontSize: "15px",
    fontFamily: "Poppins",
    borderColor: "#d1d5db",
    "&:hover": {
      borderColor: "#d1d5db",
    },
  }),
  input: (base) => ({
    ...base,
    // Prevent iOS keyboard from appearing
    caretColor: "transparent",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#0B1215",
    padding: "8px",
    "& svg": {
      width: "16px",
      height: "16px",
    },
    "&:hover": {
      color: "#0B1215",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "#000",
    fontWeight: 400,
    fontFamily: "Poppins",
    fontSize: "14px",
    lineHeight: "normal",
    letterSpacing: "0%",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#BCBCBF",
    fontWeight: 400,
    fontSize: "14px",
    fontFamily: "Poppins",
    lineHeight: "normal",
    letterSpacing: "0%",
  }),
  menu: (base) => ({
    ...base,
    maxHeight: "300px",
    borderRadius: "8px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    marginTop: "4px",
    marginBottom: "4px",
    overflow: "hidden",
    zIndex: 99999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "300px",
    padding: "8px 0",
    "::-webkit-scrollbar": {
      width: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  }),
  option: (base, state) => ({
    ...base,
    padding: "10px 12px",
    backgroundColor: state.isSelected
      ? "#6B46C1"
      : state.isFocused
      ? "#f3f4f6"
      : "white",
    color: state.isSelected ? "white" : "#1f2937",
    cursor: "pointer",
    fontFamily: "Poppins",
    "&:active": {
      backgroundColor: "#6B46C1",
    },
  }),
};

const SelectField = <
  OptionType = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>
>({
  error,
  touched,
  containerClassName = "",
  styles,
  ...props
}: SelectFieldProps<OptionType, IsMulti, Group>) => {
  // Merge custom styles with default styles
  const mergedStyles: StylesConfig<OptionType, IsMulti, Group> = styles
    ? (Object.keys(defaultStyles).reduce((acc, key) => {
        const styleKey = key as keyof StylesConfig;
        acc[styleKey] = (base: any, state: any) => {
          const defaultStyle = defaultStyles[styleKey]?.(base, state);
          const customStyle = styles?.[styleKey]?.(base, state);
          return { ...defaultStyle, ...customStyle };
        };
        return acc;
      }, {} as any) as StylesConfig<OptionType, IsMulti, Group>)
    : (defaultStyles as unknown as StylesConfig<OptionType, IsMulti, Group>);

  return (
    <div className={containerClassName}>
      <Select<OptionType, IsMulti, Group>
        styles={mergedStyles}
        {...props}
        menuPortalTarget={document.body}
        menuPosition="absolute"
        menuPlacement="bottom"
        openMenuOnFocus={false}
        blurInputOnSelect={true}
        captureMenuScroll={false}
      />
      {touched && error && (
        <div className="pt-1 pl-1 text-sm text-red-600">{error}</div>
      )}
    </div>
  );
};

export default SelectField;
